import { useEffect, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

export const PMPagination = (props) => {
  const { t } = useTranslation();
  const sentinelRef = useRef(null);
  const {
    currentCount,
    totalCount,
    page = 1,
    onPageChange,
    onLoadMore,
    perPage,
    showCount = true,
    showPages = true,
    showNextPrev = true,
    modernView = false,
    disabled = false,
  } = props;
  // currentCount = How many rows currently showing
  // totalCount = How many ACTIVE items in the database
  // page = Current page number
  // onPageChange = Callback when page changes
  // onLoadMore = Callback when user scrolls to bottom or clicks "Load more" (modernView)
  // modernView = Hide count/prev/next, use infinite scroll + "Load more" button
  // perPage = Items per page

  const itemsPerPage = perPage > 1 ? perPage : 200;
  const hasMore = (currentCount || 0) < (totalCount || 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const currentPage = Math.min(Math.max(1, parseInt(page, 10)), totalPages);

  // Fewer visible pages on mobile (3), more on desktop (5)
  const maxVisible = 5;
  let startPage = Math.max(
    1,
    currentPage - Math.floor(maxVisible / 2)
  );
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handleChangePage = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    onPageChange?.(pageNumber);
  };

  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const btnBase =
    "inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-sm font-medium transition-colors touch-manipulation select-none";

  const btnEnabled =
    "cursor-pointer text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200";
  const btnDisabled =
    "cursor-not-allowed text-gray-300 pointer-events-none";
  const pageActive =
    "bg-blue-600 text-white hover:bg-blue-600 hover:text-white";
  const pageInactive =
    "text-gray-600 hover:bg-gray-100 hover:text-gray-900";

  // Infinite scroll: observe sentinel and trigger onLoadMore when visible
  useEffect(() => {
    if (!modernView || !onLoadMore || !hasMore || disabled) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "100px", threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [modernView, onLoadMore, hasMore, disabled]);

  const handleLoadMore = () => {
    if (!disabled && hasMore && onLoadMore) onLoadMore();
  };

  // ModernView: only sentinel + "Load more" button, no count/prev/next
  if (modernView) {
    return (
      <div className="w-full py-2">
        {hasMore && (
          <>
            <div ref={sentinelRef} className="h-1" aria-hidden />
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={disabled}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("pagination.loadMore")}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <nav
        className="flex flex-wrap items-center justify-center gap-2 py-3 sm:gap-3"
        aria-label="Pagination"
      >
        {showNextPrev && (
          <button
            type="button"
            onClick={() => handleChangePage(currentPage - 1)}
            disabled={!canPrev}
            aria-label="Previous page"
            className={`${btnBase} px-3 sm:px-4 ${
              canPrev ? btnEnabled : btnDisabled
            }`}
          >
            <ChevronLeftIcon className="h-5 w-5 sm:mr-1" />
            <span className="hidden sm:inline">{t("pagination.previous")}</span>
          </button>
        )}

        {showPages && totalPages > 1 && (
          <div className="flex items-center gap-1 sm:gap-0.5">
            {startPage > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => handleChangePage(1)}
                  className={`${btnBase} ${btnEnabled} px-2.5 sm:px-3`}
                >
                  1
                </button>
                {startPage > 2 && (
                  <span className="px-1 text-gray-400 sm:px-2">…</span>
                )}
              </>
            )}
            {pages.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handleChangePage(p)}
                aria-current={p === currentPage ? "page" : undefined}
                className={`${btnBase} ${
                  p === currentPage ? pageActive : pageInactive
                } px-2.5 sm:px-3`}
              >
                {p}
              </button>
            ))}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <span className="px-1 text-gray-400 sm:px-2">…</span>
                )}
                <button
                  type="button"
                  onClick={() => handleChangePage(totalPages)}
                  className={`${btnBase} ${btnEnabled} px-2.5 sm:px-3`}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
        )}

        {showNextPrev && (
          <button
            type="button"
            onClick={() => handleChangePage(currentPage + 1)}
            disabled={!canNext}
            aria-label="Next page"
            className={`${btnBase} px-3 sm:px-4 ${
              canNext ? btnEnabled : btnDisabled
            }`}
          >
            <span className="hidden sm:inline">{t("pagination.next")}</span>
            <ChevronRightIcon className="h-5 w-5 sm:ml-1" />
          </button>
        )}
      </nav>

      {showCount && (
        <p className="text-center text-sm text-gray-500">
          {t("pagination.showing", {
            current: currentCount > 0 ? currentCount : 0,
            total: totalCount,
          })}
        </p>
      )}
    </div>
  );
};


